import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { UploadCloud, X } from "lucide-react";
import { track } from "@/lib/analytics";

const CATEGORIES = ["femme", "homme", "enfants", "chaussures", "sacs", "accessoires", "vintage"];

export default function Sell() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: "", description: "", price: "", category: "femme",
    size: "", condition: "very_good", brand: "", color: "",
  });
  const [images, setImages] = useState([]);
  const [saving, setSaving] = useState(false);

  if (user === false) {
    navigate("/login?next=/sell");
    return null;
  }

  const readFile = (file) => new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 6 - images.length);
    for (const f of files) {
      if (f.size > 5 * 1024 * 1024) { toast.error(`${f.name} ${t("sell.err_size")}`); continue; }
      const url = await readFile(f);
      setImages((prev) => [...prev, url]);
    }
    e.target.value = "";
  };

  const removeImage = (i) => setImages((prev) => prev.filter((_, idx) => idx !== i));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error(t("sell.err_title")); return; }
    if (!form.price || Number(form.price) <= 0) { toast.error(t("sell.err_price")); return; }
    if (images.length === 0) { toast.error(t("sell.err_photos")); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/listings", {
        ...form, price: Number(form.price), images,
      });
      track("listing_created", { category: form.category, price: Number(form.price) });
      toast.success(t("sell.success"));
      navigate(`/item/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 fade-in">
      <div className="overline text-primary">{t("sell.kicker")}</div>
      <h1 className="font-display text-4xl mt-2">{t("sell.title")}</h1>
      <p className="mt-2 text-muted-foreground">{t("sell.subtitle")}</p>

      <form onSubmit={onSubmit} className="mt-10 space-y-8" data-testid="sell-form">
        <div>
          <Label>{t("sell.photos_label")} ({images.length}/6)</Label>
          <div className="mt-3 grid grid-cols-3 sm:grid-cols-6 gap-3">
            {images.map((src, i) => (
              <div key={i} className="relative aspect-square rounded-lg overflow-hidden bg-muted">
                <img src={src} alt="" className="w-full h-full object-cover" />
                <button
                  type="button" onClick={() => removeImage(i)}
                  data-testid={`remove-image-${i}`}
                  className="absolute top-1 right-1 w-6 h-6 rounded-full bg-background/90 flex items-center justify-center hover:bg-destructive hover:text-destructive-foreground transition-colors"
                ><X className="w-3.5 h-3.5" /></button>
              </div>
            ))}
            {images.length < 6 && (
              <label
                data-testid="image-upload"
                className="aspect-square rounded-lg border-2 border-dashed border-border hover:border-primary hover:bg-accent/30 flex flex-col items-center justify-center gap-1 cursor-pointer transition-colors text-muted-foreground"
              >
                <UploadCloud className="w-5 h-5" />
                <span className="text-xs">{t("sell.add_photo")}</span>
                <input type="file" accept="image/*" multiple hidden onChange={onFiles} />
              </label>
            )}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-5">
          <Field label={t("sell.field_title")}>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder={t("sell.placeholder_title")} data-testid="input-title" className="h-11" />
          </Field>
          <Field label={t("sell.field_price")}>
            <Input type="number" step="0.01" min="0" value={form.price}
              onChange={(e) => setForm({ ...form, price: e.target.value })}
              placeholder={t("sell.placeholder_price")} data-testid="input-price" className="h-11" />
          </Field>
          <Field label={t("sell.field_category")}>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger className="h-11" data-testid="input-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c}>{t(`browse.cat_${c}`)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label={t("sell.field_condition")}>
            <Select value={form.condition} onValueChange={(v) => setForm({ ...form, condition: v })}>
              <SelectTrigger className="h-11" data-testid="input-condition">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {["new","like_new","very_good","good","fair"].map((c) => (
                  <SelectItem key={c} value={c}>{t(`condition.${c}`)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label={t("sell.field_size")}>
            <Input value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })}
              placeholder={t("sell.placeholder_size")} data-testid="input-size" className="h-11" />
          </Field>
          <Field label={t("sell.field_brand")}>
            <Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })}
              placeholder={t("sell.placeholder_brand")} data-testid="input-brand" className="h-11" />
          </Field>
          <Field label={t("sell.field_color")}>
            <Input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })}
              placeholder={t("sell.placeholder_color")} data-testid="input-color" className="h-11" />
          </Field>
        </div>

        <Field label={t("sell.field_description")}>
          <Textarea rows={5} value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder={t("sell.placeholder_description")} data-testid="input-description" />
        </Field>

        <div className="pt-4 flex items-center gap-3">
          <Button type="submit" size="lg" className="rounded-full h-12 px-8"
            disabled={saving} data-testid="submit-listing">
            {saving ? t("sell.submitting") : t("sell.submit")}
          </Button>
          <Button type="button" variant="ghost" onClick={() => navigate("/")}
            data-testid="cancel-listing">
            {t("sell.cancel")}
          </Button>
        </div>
      </form>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-2">
    <Label className="text-sm">{label}</Label>
    {children}
  </div>
);
